import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";


// ✅ 1. CREATE PLAN
export const createPlan = createAsyncThunk(
    "plan/create",
    async (planData, { rejectWithValue }) => {
        try {
            const { data } = await api.post("/plans/create", planData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error creating plan" });
        }
    }
);


// ✅ 2. GET ALL PLANS
export const getAllPlans = createAsyncThunk(
    "plan/getAll",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await api.get("/plans/all");
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error fetching plans" });
        }
    }
);


// ✅ 3. GET SINGLE PLAN
export const getPlanById = createAsyncThunk(
    "plan/getById",
    async (id, { rejectWithValue }) => {
        try {
            const { data } = await api.get(`/plans/single/${id}`);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error fetching plan" });
        }
    }
);


// ✅ 4. UPDATE PLAN
export const updatePlan = createAsyncThunk(
    "plan/update",
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const { data } = await api.put(`/plans/update/${id}`, formData);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error updating plan" });
        }
    }
);


// ✅ 5. DELETE PLAN
export const deletePlan = createAsyncThunk(
    "plan/delete",
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/plans/delete/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error deleting plan" });
        }
    }
);


// 🔥 INITIAL STATE
const initialState = {
    plans: [],
    singlePlan: null,
    loading: false,
    error: null,
    success: null
};


// 🧠 SLICE
const planSlice = createSlice({
    name: "plan",
    initialState,
    reducers: {
        clearPlanMessages: (state) => {
            state.error = null;
            state.success = null;
        }
    },
    extraReducers: (builder) => {
        builder

            // ✅ CREATE
            .addCase(createPlan.pending, (state) => {
                state.loading = true;
            })
            .addCase(createPlan.fulfilled, (state, action) => {
                state.loading = false;
                state.success = action.payload.message;
                state.plans.push(action.payload.data);
            })
            .addCase(createPlan.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })


            // ✅ GET ALL
            .addCase(getAllPlans.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllPlans.fulfilled, (state, action) => {
                state.loading = false;
                state.plans = action.payload;
            })
            .addCase(getAllPlans.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })


            // ✅ GET SINGLE
            .addCase(getPlanById.pending, (state) => {
                state.loading = true;
            })
            .addCase(getPlanById.fulfilled, (state, action) => {
                state.loading = false;
                state.singlePlan = action.payload;
            })
            .addCase(getPlanById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })


            // ✅ UPDATE
            .addCase(updatePlan.fulfilled, (state, action) => {
                state.success = "Plan updated successfully";
                state.plans = state.plans.map((plan) =>
                    plan._id === action.payload._id ? action.payload : plan
                );
            })
            .addCase(updatePlan.rejected, (state, action) => {
                state.error = action.payload?.message;
            })


            // ✅ DELETE
            .addCase(deletePlan.fulfilled, (state, action) => {
                state.success = "Plan deleted successfully";
                state.plans = state.plans.filter(
                    (plan) => plan._id !== action.payload
                );
            })
            .addCase(deletePlan.rejected, (state, action) => {
                state.error = action.payload?.message;
            });

    },
});

export const { clearPlanMessages } = planSlice.actions;
export default planSlice.reducer;